import { useMemo, useState, useCallback } from 'react';
import pb from '../lib/pb';
import { saveSprite } from '../services/spriteService';

type Params = {
  pixels: number[][] | null;
  colors: string[];
  secondaryColors?: string[];
  onSaved?: () => void;
};

function signature(pixels: number[][] | null, colors: string[], secondaryColors?: string[]) {
  if (!pixels) return 'nil';
  return JSON.stringify({ p: pixels, c: colors, s: secondaryColors }); // include secondary in sig
}

export function useSpriteSave({ pixels, colors, secondaryColors, onSaved }: Params) {
  const [saving, setSaving] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [lastSavedSig, setLastSavedSig] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentSig = useMemo(() => signature(pixels, colors, secondaryColors), [pixels, colors, secondaryColors]);
  const canSave = !!pixels && currentSig !== lastSavedSig && !saving;

  // Skapa liten PNG-preview (1px per sprite‑pixel) med stöd för secondary
  const buildPreviewBlob = useCallback(async () => {
    if (!pixels) return null;
    const h = pixels.length;
    const w = pixels[0]?.length ?? h;
    const cvs = document.createElement('canvas');
    cvs.width = w; cvs.height = h;
    const ctx = cvs.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;

    const resolve = (ci: number) => {
      const isSec = (ci & 0x80) !== 0 || ci < 0;
      const base = ci < 0 ? (ci + 16) & 0x0f : (ci & 0x0f);
      const sec = secondaryColors ?? colors;
      return isSec ? (sec[base] ?? colors[base] ?? '#000') : (colors[base] ?? '#000');
    };

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const ci = pixels[y][x] ?? 0;
        ctx.fillStyle = resolve(ci);
        ctx.fillRect(x, y, 1, 1);
      }
    }
    return new Promise<Blob>((res, rej) =>
      cvs.toBlob(b => (b ? res(b) : rej(new Error('blob failed'))), 'image/png') as any
    );
  }, [pixels, colors, secondaryColors]);

  const doSave = useCallback(async (name?: string) => {
    if (!pixels) return;
    setSaving(true);
    try {
      setError(null);
      const safeName = (name?.trim() || 'untitled');
      const blob = await buildPreviewBlob();
      await saveSprite({
        name: safeName,
        pixels,            // innehåller 0..15 och 128..143
        palette: colors,   // primär-palett; secondary hanteras vid render
        width: pixels[0]?.length ?? pixels.length,
        height: pixels.length,
        previewBlob: blob || undefined,
      });
      setLastSavedSig(currentSig);
      onSaved?.();
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
      console.error('[save sprite]', e);
    } finally {
      setSaving(false);
    }
  }, [pixels, colors, buildPreviewBlob, currentSig, onSaved]);

  const handleSaveClick = () => {
    if (!pixels || saving) return;
    if (!pb.authStore.isValid) { setShowAuth(true); return; }
    setShowSaveDialog(true);
  };

  const handleAuthSuccess = () => { setShowAuth(false); setShowSaveDialog(true); };
  const closeAuth = () => setShowAuth(false);
  const closeSaveDialog = () => setShowSaveDialog(false);

  const confirmSave = useCallback(async (name?: string) => {
    await doSave(name);
    setShowSaveDialog(false);
  }, [doSave]);

  return {
    saving,
    canSave,
    error,
    handleSaveClick,
    showAuth,
    handleAuthSuccess,
    closeAuth,
    showSaveDialog,
    closeSaveDialog,
    confirmSave,
  };
}